import {
  ActionGroup,
  Button,
  Flex,
  FlexItem,
  Form,
  Modal,
  ModalVariant,
  TextArea,
  Title,
} from '@patternfly/react-core'
import { InfoCircleIcon } from '@patternfly/react-icons'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'

import type { IProgram } from '@app/domain/program/program.interface'
import * as ProgramScreenState from '@app/state/program_screen/state'
import { InfoText } from '@app/utils/info-text-container'
import { ActionLink } from '@app/utils/styles/action-link'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  information: string
}
export const InformationModal = ({
  isOpen,
  onClose,
  title,
  information,
}: ModalProps) => {
  return (
    <Modal
      variant={ModalVariant.small}
      title={title}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div dangerouslySetInnerHTML={{ __html: information }} />
    </Modal>
  )
}

interface Props {
  program: IProgram
}

const configurableMessageContentInfo = `
  <p>The following variables can be used in the content:</p>
   <ul style="margin-left: 20px;">
    <li>Patient first name as <code>{{patientFirstName}}</code></li>
    <li>Patient given names as <code>{{patientGivenNames}}</code></li>
    <li>Patient last name as <code>{{patientLastName}}</code></li>
    <li>A link to the workflow as <code>{{surveyUrl}}</code></li>
    <li>The expiration date of the workflow as <code>{{expiresAtDate}}</code></li>
    <li>The expiration time of the workflow as <code>{{expiresAtTime}}</code></li>
    <li>The combined expiration date and time of the workflow as <code>{{expiresAtDateAndTime}}</code></li>
  </ul>
  <br />

  <p>For example:</p>
  <p>Dear {{patientFirstName}}, please complete <a href="{{surveyUrl}}">this survey</a>.</p>
`

//for signature, workflow complete page and info page
const configurableContentInfo = `

  <p>The following variables can be used in the content:</p>
   <ul style="margin-left: 20px;">
    <li>Patient first name as <code>{{patientFirstName}}</code></li>
    <li>Patient given names as <code>{{patientGivenNames}}</code></li>
    <li>Patient last name as <code>{{patientLastName}}</code></li>
  </ul>
  <br />

  <p>For example:</p>
  <p><em>"Dear {{patientFirstName}}, please complete this survey."</em></p>
`

export const CustomProgramContent = ({ program }: Props) => {
  const dispatch = useDispatch()

  const [isModalOpen, setModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState({
    title: '',
    information: '',
  })

  const openModal = (title: string, information: string) => {
    setModalContent({ title, information })
    setModalOpen(true)
  }

  const closeModal = () => setModalOpen(false)

  const [contents, setContents] = useState({
    interactionInformationContent:
      program.interactionInformationContent || null,
    interactionCompleteContent: program.interactionCompleteContent || null,
    interactionNoticeEmailContent:
      program.interactionNoticeEmailContent || null,
    interactionNoticeSmsContent: program.interactionNoticeSmsContent || null,
  })

  const handleContentChange = (field: keyof typeof contents, value: string) => {
    setContents((prev) => ({ ...prev, [field]: value }))
  }

  const handleReset = (field: keyof typeof contents) => {
    setContents((prev) => ({ ...prev, [field]: program[field] || null }))
  }

  const handleUpdate = (field: keyof typeof contents) => {
    const trimmedContent = contents[field].trim()
    dispatch(
      ProgramScreenState.actions.updateProgram({
        ...program,
        [field]: trimmedContent === '' ? null : trimmedContent,
      }),
    )
  }

  const isContentUnchanged = useCallback(
    (field: keyof typeof contents) => {
      const currentContent = contents[field] ? contents[field].trim() : ''
      const originalContent = program[field]
        ? String(program[field]).trim()
        : ''
      return currentContent === originalContent
    },
    [contents, program],
  )

  return (
    <Form>
      <InformationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={modalContent.title}
        information={modalContent.information}
      />
      <Flex
        direction={{ default: 'column' }}
        spaceItems={{ default: 'spaceItemsMd' }}
      >
        <FlexItem style={{ maxWidth: '50%' }}>
          <Title headingLevel="h2" size="lg">
            Interaction Information Content{' '}
            <ActionLink
              onClick={() =>
                openModal(
                  'Interaction Information Content',
                  configurableContentInfo,
                )
              }
              style={{ fontSize: '1rem' }}
            >
              <InfoCircleIcon />
            </ActionLink>
          </Title>
          <InfoText
            content="This shows on the first screen patients see after authenticating. It is shown every time. May contain HTML content"
            maxWidth="100%"
          />
          <TextArea
            rows={10}
            value={contents.interactionInformationContent}
            onChange={(value) =>
              handleContentChange('interactionInformationContent', value)
            }
          />
          <ActionGroup style={{ float: 'right', marginTop: 10 }}>
            <Button
              variant="secondary"
              onClick={() => handleReset('interactionInformationContent')}
              isDisabled={isContentUnchanged('interactionInformationContent')}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={() => handleUpdate('interactionInformationContent')}
              isDisabled={isContentUnchanged('interactionInformationContent')}
            >
              Save
            </Button>
          </ActionGroup>
        </FlexItem>

        <FlexItem style={{ maxWidth: '50%' }}>
          <Title headingLevel="h2" size="lg">
            Interaction Completion Content{' '}
            <ActionLink
              onClick={() =>
                openModal(
                  'Interaction Completion Content',
                  configurableContentInfo,
                )
              }
              style={{ fontSize: '1rem' }}
            >
              <InfoCircleIcon />
            </ActionLink>
          </Title>
          <InfoText
            content="This shows on the last screen patients see after authenticating. It is shown every time. May contain HTML content"
            maxWidth="100%"
          />
          <TextArea
            rows={10}
            value={contents.interactionCompleteContent}
            onChange={(value) =>
              handleContentChange('interactionCompleteContent', value)
            }
          />
          <ActionGroup style={{ float: 'right', marginTop: 10 }}>
            <Button
              variant="secondary"
              onClick={() => handleReset('interactionCompleteContent')}
              isDisabled={isContentUnchanged('interactionCompleteContent')}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={() => handleUpdate('interactionCompleteContent')}
              isDisabled={isContentUnchanged('interactionCompleteContent')}
            >
              Save
            </Button>
          </ActionGroup>
        </FlexItem>
      </Flex>

      <Flex direction={{ default: 'column' }} style={{ marginTop: '2em' }}>
        <FlexItem>
          <Title headingLevel="h2" size="lg">
            Interaction Notification Message Content{' '}
            <ActionLink
              onClick={() =>
                openModal(
                  'Interaction Notification Content',
                  configurableMessageContentInfo,
                )
              }
              style={{ fontSize: '1rem' }}
            >
              <InfoCircleIcon />
            </ActionLink>
          </Title>
          <InfoText
            content="This is sent to the patient as the initial notification for the interaction. It is sent as email and/or SMS; email may contain HTML content."
            maxWidth="100%"
          />
        </FlexItem>

        <Flex
          style={{
            justifyContent: 'space-between',
            gap: '1em',
            marginTop: '1em',
          }}
        >
          <FlexItem style={{ flex: '1' }}>
            <Title headingLevel="h3" size="md">
              Email Content
            </Title>
            <TextArea
              rows={10}
              value={contents.interactionNoticeEmailContent}
              onChange={(value) =>
                handleContentChange('interactionNoticeEmailContent', value)
              }
            />
            <ActionGroup style={{ float: 'right', marginTop: 10 }}>
              <Button
                variant="secondary"
                onClick={() => handleReset('interactionNoticeEmailContent')}
                isDisabled={isContentUnchanged('interactionNoticeEmailContent')}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={() => handleUpdate('interactionNoticeEmailContent')}
                isDisabled={isContentUnchanged('interactionNoticeEmailContent')}
              >
                Save
              </Button>
            </ActionGroup>
          </FlexItem>

          <FlexItem style={{ flex: '1' }}>
            <Title headingLevel="h3" size="md">
              SMS Content
            </Title>
            <TextArea
              rows={10}
              value={contents.interactionNoticeSmsContent}
              onChange={(value) =>
                handleContentChange('interactionNoticeSmsContent', value)
              }
            />
            <ActionGroup style={{ float: 'right', marginTop: 10 }}>
              <Button
                variant="secondary"
                onClick={() => handleReset('interactionNoticeSmsContent')}
                isDisabled={isContentUnchanged('interactionNoticeSmsContent')}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                onClick={() => handleUpdate('interactionNoticeSmsContent')}
                isDisabled={isContentUnchanged('interactionNoticeSmsContent')}
              >
                Save
              </Button>
            </ActionGroup>
          </FlexItem>
        </Flex>
      </Flex>
    </Form>
  )
}
